import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ModalService } from '../../../../core/services/modal.service';
import { AuthService } from '../../../../core/services/auth.service';
import { SwalService } from '../../../../core/services/swal.service';
import {
  LucideAngularModule,
  EyeIcon,
  EyeOffIcon,
  Loader2Icon,
} from 'lucide-angular';
import { Router } from '@angular/router';
import { UrlService } from '../../../../core/services/url.service';

@Component({
  selector: 'app-force-change-password-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule],
  templateUrl: './force-change-password-modal.component.html',
})
export class ForceChangePasswordModalComponent {
  readonly EyeIcon = EyeIcon;
  readonly EyeOffIcon = EyeOffIcon;
  readonly Loader2Icon = Loader2Icon;

  passwordForm = new FormGroup(
    {
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(30),
        Validators.pattern(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{8,}$/
        ),
      ]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: this.passwordMatchValidator }
  );

  showNewPassword = false;
  showConfirmPassword = false;
  isLoading = false;

  private modalService = inject(ModalService);
  private authService = inject(AuthService);
  private swalService = inject(SwalService);
  private router = inject(Router);
  private urlService = inject(UrlService);

  passwordMatchValidator(
    control: AbstractControl
  ): { [key: string]: boolean } | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (
      newPassword &&
      confirmPassword &&
      newPassword.value !== confirmPassword.value
    ) {
      return { passwordMismatch: true };
    }
    return null;
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (this.passwordForm.valid) {
      this.isLoading = true;

      const formValue = this.passwordForm.value;
      const resetPasswordToken = this.authService.getResetPasswordToken();

      if (!resetPasswordToken) {
        this.isLoading = false;
        this.swalService.error(
          'Erreur',
          'Token de réinitialisation manquant. Veuillez vous reconnecter.'
        );
        return;
      }

      const changePasswordData = {
        newPassword: formValue.newPassword || '',
        otpId: resetPasswordToken, // Utiliser le token reset_password comme otpId
        phoneNumber: this.authService.getUserData()?.phoneNumbers?.[0]?.number,
      };

      this.authService.changePassword(changePasswordData).subscribe({
        next: (response) => {
          this.isLoading = false;

          // Nettoyer le token de reset_password
          this.authService.clearResetPasswordToken();

          this.swalService.success(
            'Succès',
            'Votre mot de passe a été changé avec succès.'
          );

          // Fermer le modal
          this.modalService.closeAll();

          // Rediriger vers /passport avec le code pays
          this.router.navigate(this.urlService.buildRouteSegments('passport'));
        },
        error: (error) => {
          this.isLoading = false;
          this.swalService.error(
            'Erreur',
            error.error?.message ||
              'Une erreur est survenue lors du changement de mot de passe.'
          );
        },
      });
    } else {
      this.passwordForm.markAllAsTouched();
    }
  }

  get passwordMismatchError(): boolean {
    return (
      this.passwordForm.hasError('passwordMismatch') &&
      this.passwordForm.get('confirmPassword')?.touched === true
    );
  }

  get newPasswordError(): string {
    const control = this.passwordForm.get('newPassword');
    if (control?.errors && control.touched) {
      if (control.errors['required'])
        return 'Le nouveau mot de passe est requis';
      if (control.errors['minlength'])
        return 'Le mot de passe doit contenir au moins 8 caractères';
      if (control.errors['maxlength'])
        return 'Le mot de passe ne doit pas dépasser 30 caractères';
      if (control.errors['pattern'])
        return 'Le mot de passe doit contenir au moins une majuscule, une minuscule, un chiffre et un caractère spécial (@$!%*?&)';
    }
    return '';
  }

  get confirmPasswordError(): string {
    const control = this.passwordForm.get('confirmPassword');
    if (control?.errors && control.touched) {
      if (control.errors['required'])
        return 'La confirmation du mot de passe est requise';
    }
    return '';
  }
}
